import { BookingModel } from './booking.model';
import { LocationModel } from './location.model';
import {
  Price,
  ServicePricingOptionModel,
  ServiceModel,
  ServiceAddOnModel,
} from './service.model';

export interface StatusHistoryItemModel {
  date: Date;
  status: string;
  user: string;
}

export interface PaymentModel {
  totalPrice: Price;
  paymentMethod: string;
  refund?: {
    canceledMinutesBefore: number;
    cancellationPolicy: string | null;
    refundPercentage: number;
    amount: {
      currency: string;
      value: number;
    };
    refundId: string;
    date: string;
  };
  stripe: {
    clientSecret: string;
    customerSessionClientSecret: string;
  };
  status: string;
}

export interface GuestModel {
  externalId: string;
  location: LocationModel;
  email: string;
  firstName: string;
  lastName: string;
}

export interface ServiceRequestModel {
  _id: string;
  assets: {
    metadata: {
      createdAt: string;
      updatedAt: string;
    };
    _id: string;
    assetType: string;
    branch: string;
    name: string;
    enabled: boolean;
    quantity: number;
  }[];
  orderId?: string;
  consecutiveSlotsQuantity?: number;
  date: {
    from: Date | string;
    to: Date | string;
  };
  totalPrice?: Price;
  payment?: PaymentModel;
  paymentUrl?: string;
  service: ServiceModel;
  quantity: number;
  status: string;
  statusHistory: StatusHistoryItemModel[];
  createdFrom: string;
  guest: GuestModel;
  executor?: {
    user?: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    info?: string;
  };
  booking: {
    externalId: string;
    apartment: string;
    bookingInfo: BookingModel;
  };
  specialRequest?: string;
  additionalInformation?: AdditionalInformationModel;
  location: LocationModel;
  metadata: {
    createdAt: Date;
    updatedAt: Date;
  };
  promoCode?: string;
  pricingAddOns?: ServiceRequestPricingAddOnModel[];
  pricingOptions?: ServiceRequestPricingOptionsModel[];
  includedServices?: ServiceModel[];
  quota?: number;
  serviceQuota?: number;
  expireInDays?: number;
  expirationDate?: string | Date;
  voucher?: {
    serviceRequest: ServiceRequestModel;
    usedQuota: number;
    amount: Price;
  };
  /** Answers of the service preFetchDynamicFields (integration services). */
  preFetchDynamicFields?: Record<string, any>;
}

export interface ServiceRequestPricingAddOnModel {
  serviceAddOn: ServiceAddOnModel;
  quantity: number;
  amount: Price;
  baseAmount: Price;
}

export interface ServiceRequestPricingAddOnsModel {
  addOns: ServiceRequestAddOn[];
  quantity?: number;
  amount: Price;
}

export interface ServiceRequestAddOn {
  serviceAddOn: ServiceAddOnModel;

  quantity: number;

  amount: Price;
}

export interface ServiceRequestPricingOptionsModel {
  servicePricingOption: ServicePricingOptionModel;
  amount: Price;
  quantity: number;
}

export interface CheckPendingServiceRequestGuestModel {
  externalId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  location?: {
    _id?: string;
  };
}

export interface CheckPendingServiceRequestModel {
  date: {
    from: Date | string;
    to?: Date | string;
  };
  service?: string;
  guest?: CheckPendingServiceRequestGuestModel;
}

export interface PostServiceRequestModel {
  date: {
    from: Date;
    to: Date;
  };
  slotId: string;
  /** Id of the cached provider quote (integration services). */
  quoteId?: string;
  /** Answers of the service preFetchDynamicFields (integration services). */
  preFetchDynamicFields?: Record<string, any>;
  booking?: {
    externalId: string;
    apartmentId: string;
  };
  quantity: number;
  specialRequest?: string;
  promoCode?: string;
  location?: LocationModel;
  guest?: {
    location?: {
      _id?: string;
    };
  };
  addOns?: PostServiceRequestAddOnsModel[];
  pricingOptions?: PostServiceRequestPricingOptionsModel[];
  voucher?: string;
  additionalInformation?: AdditionalInformationModel;
}

export interface PostServiceRequestPricingOptionsModel {
  _id: string;
  quantity: number;
}

export interface PostServiceRequestAddOnsModel {
  _id: string;
  quantity?: number;
}

export interface CalendarEvent {
  title: string;
  start: Date | string;
  end: Date | string;
  className: string;
  allDay: boolean;
  extendedProps: {
    serviceRequest: ServiceRequestModel;
  };
}

/** Raw key–value answers from `survey.data` */
export type ServiceRequestAdditionalInformationAnswerModel = Record<
  string,
  unknown
>;

/** Items from `survey.getPlainData()` */
export interface AdditionalInformationDataModel {
  name: string;
  title?: string;
  value?: unknown;
  displayValue?: unknown;
  isNode?: boolean;
  questionType?: string;
  [key: string]: unknown; // keep flexibility for SurveyJS extras
}

/** Your persisted payload */
export interface AdditionalInformationModel {
  /** Canonical answers used to repopulate the form */
  answer: ServiceRequestAdditionalInformationAnswerModel;
  /** Optional snapshot for reporting/audits */
  data?: AdditionalInformationDataModel[];
}
